import { type FC, useCallback } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { DoseVolumeColumn } from '@/components/UI/DataTableV2/DataTable.model';
import Radio from '@/components/UI/FormElements/Radio/Radio';
import { DOSE_VOLUME_UNITS, type DoseVolumeUnit, isDoseVolumeUnit } from '@/model/Dose.model';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';

interface FormData {
  unit: DoseVolumeUnit | null;
}

interface EditDoseVolumeUnitProps {
  column: DoseVolumeColumn;
  onSave: (unit: DoseVolumeUnit) => Promise<void> | void;
  onCancel: () => void;
}

/**
 * Chooses the unit a dosing sheet's Volume column displays. Values are converted on read, so no
 * stored dose is rewritten.
 */
export const EditDoseVolumeUnit: FC<EditDoseVolumeUnitProps> = ({ column, onSave, onCancel }) => {
  const formMethods = useForm<FormData>({
    mode: 'onChange',
    defaultValues: { unit: isDoseVolumeUnit(column.unit) ? column.unit : null },
  });

  const handleSubmit = useCallback(
    async (formData: FormData) => {
      if (formData.unit === null) {
        return;
      }
      await onSave(formData.unit);
      onCancel();
    },
    [onSave, onCancel]
  );

  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Edit column" closeModal={onCancel} />
      <form
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        data-test-component="EditDoseVolumeUnit"
        data-test-element="form"
      >
        <Controller
          control={formMethods.control}
          name="unit"
          rules={{ required: true }}
          render={({ field }) => (
            <fieldset className="pa3 bn ma0" data-test-element="unit-field">
              <legend className="db mb2">Volume unit</legend>
              {DOSE_VOLUME_UNITS.map((unit) => (
                <Radio
                  key={unit}
                  id={`dose-volume-unit-${unit}`}
                  className="pb2"
                  value={unit}
                  name={field.name}
                  label={unit}
                  checked={field.value === unit}
                  onChange={() => field.onChange(unit)}
                  keyboardFocusable
                />
              ))}
              <small className="db">Existing volumes are converted; no dose is changed.</small>
            </fieldset>
          )}
        />
      </form>
      <ModalActions
        className="pa3 bt b--moon-gray"
        onSubmit={formMethods.handleSubmit(handleSubmit)}
        submitBtnText="Save"
        // Not gated on isDirty: a column with no unit yet must allow saving the first option picked.
        submitButtonProps={{ disabled: !formMethods.formState.isValid || formMethods.formState.isSubmitting }}
        onCancel={onCancel}
        cancelBtnText="Cancel"
      />
    </ModalContainer>
  );
};
